/**
 * "Valuation Stage View" table for the Plot Matrix (JSON) tab — one column
 * per Valuation Stage (sub-columns collapsed/summed), matching
 * `PlotMatrix.tsx`'s own Stage View. Built from the same JSON `columns`/
 * `rows` the Plot View table renders, via `buildPlotMatrixStageView`
 * (`plotMatrixJsonStageView.ts`) — no tender state, no API calls here.
 */
import React, { useMemo } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatCurrency } from "../../../../utils/calculations";
import type { PlotMatrixCategoryColumn, PlotMatrixRow } from "../../../../utils/plotMatrixCategoryGenerator";
import {
  buildPlotMatrixStageView,
  type PlotMatrixStageViewRow,
} from "../../../../utils/plotMatrixJsonStageView";
import {
  insertGarageSeparatorRow,
  type PlotMatrixSeparatorRow,
} from "../../../../utils/plotMatrixRowSeparator";

export interface PlotMatrixJsonStageTableProps {
  columns: PlotMatrixCategoryColumn[];
  rows: PlotMatrixRow[];
}

type DisplayRow = PlotMatrixStageViewRow | PlotMatrixSeparatorRow;

const isSeparatorRow = (record: DisplayRow): record is PlotMatrixSeparatorRow =>
  (record as PlotMatrixSeparatorRow)._isSeparator === true;

const PlotMatrixJsonStageTable: React.FC<PlotMatrixJsonStageTableProps> = ({ columns, rows }) => {
  const stageView = useMemo(() => buildPlotMatrixStageView(columns, rows), [columns, rows]);
  const rowsWithSeparator = useMemo<DisplayRow[]>(
    () => insertGarageSeparatorRow(stageView.rows),
    [stageView.rows],
  );
  const totalColSpan = 2 + stageView.stageNames.length + 1;

  const tableColumns = useMemo<ColumnsType<DisplayRow>>(
    () => [
      {
        title: "Plot No",
        key: "plotNo",
        dataIndex: "plotNo",
        fixed: "left",
        width: 100,
        onCell: (record: DisplayRow) =>
          isSeparatorRow(record) ? { colSpan: totalColSpan } : {},
        render: (value: string, record: DisplayRow) => {
          if (isSeparatorRow(record)) {
            return (
              <strong style={{ fontSize: 13, color: "#374151", letterSpacing: "0.05em" }}>
                GARAGES
              </strong>
            );
          }
          return value;
        },
      },
      {
        title: "House Type",
        key: "housetype",
        dataIndex: "housetype",
        fixed: "left",
        width: 150,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
      },
      ...stageView.stageNames.map((stage) => ({
        title: (
          <Tooltip title={stage}>
            <span
              style={{
                fontWeight: 700,
                color: "#1f2937",
                fontSize: 14,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center" as const,
              }}
            >
              {stage}
            </span>
          </Tooltip>
        ),
        key: `stage-${stage}`,
        align: "right" as const,
        width: 160,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
        render: (_: unknown, record: DisplayRow) => {
          if (isSeparatorRow(record)) return null;
          return formatCurrency(record.stageTotals[stage] ?? 0);
        },
      })),
      {
        title: "Row Total",
        key: "rowTotal",
        fixed: "right",
        width: 150,
        align: "right" as const,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
        render: (_: unknown, record: DisplayRow) => {
          if (isSeparatorRow(record)) return null;
          return <strong>{formatCurrency(record.rowTotal)}</strong>;
        },
      },
    ],
    [stageView.stageNames, totalColSpan],
  );

  return (
    <Table<DisplayRow>
      className="plot-matrix-json-stage-table"
      columns={tableColumns}
      dataSource={rowsWithSeparator}
      rowKey="key"
      pagination={false}
      scroll={{ x: "max-content" }}
      bordered
      size="small"
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <strong>GRAND TOTAL</strong>
            </Table.Summary.Cell>
            {stageView.stageNames.map((stage, idx) => (
              <Table.Summary.Cell key={stage} index={idx + 2} align="right">
                <strong>{formatCurrency(stageView.stageTotals[stage] ?? 0)}</strong>
              </Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={stageView.stageNames.length + 2} align="right">
              <strong>{formatCurrency(stageView.grandTotal)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
};

export default PlotMatrixJsonStageTable;
